import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { queryKeys } from './queries';
import type {
    BulkClearFlatworkScopeResult,
    BulkCompleteFlatworkArgs,
    BulkCompleteFlatworkResult,
    FlatworkEvent,
    FlatworkType,
} from './database.types';

// -----------------------------------------------------------------------------
// All flatwork schedule records. A lot can now have several rows (one per type
// on the same date), so the board groups these by job_id into a list.
//
// Fetched in pages, ordered, and never in one unbounded request.
//
// This used to be `.from('flatwork_events').select('*')` with no limit and no
// order. On 15 September the table held 1032 rows against PostgREST's 1000-row
// max-rows cap, so 32 rows silently never arrived. BoardPage decides the
// Active/Completed split client-side through isJobCompleted, which reads
// `flatworkByJob.get(job.id) ?? []`, and isFlatworkDone treats an empty list as
// not done. So any lot whose rows fell past the cap stayed on the active board
// no matter what the database said.
//
// It was self-inflicting, which is why it looked like every lot. complete_flatwork
// is an UPDATE; Postgres writes a new tuple version for an updated row, usually
// later in the heap, and an unordered select comes back in physical order. So
// marking a lot complete moved its rows to the end of the table and out of the
// fetch window. Andrea, 14 Sep: "Nonc of them are letting me mark complete to
// move off the board." Lots 543 and 542 were correct in the database, complete
// since 8 and 11 September, and still sat on her board.
//
// Raising max-rows in the Data API settings also clears it, but that is a
// ceiling somebody can lower again, and the next thousand rows are a matter of
// time. Paging is the fix; the setting is not load-bearing.
//
// PAGE stays under any sane cap, and the loop advances by rows actually
// returned rather than by PAGE, so a server cap lower than PAGE cannot make it
// skip. count: 'exact' is what terminates it, rather than inferring the end
// from a short page.
// -----------------------------------------------------------------------------
export function useFlatworkEvents() {
    return useQuery({
        queryKey: ['flatworkEvents'],
        queryFn: async (): Promise<FlatworkEvent[]> => {
            const PAGE = 500;
            const all: FlatworkEvent[] = [];
            let from = 0;

            for (;;) {
                const { data, error, count } = await supabase
                    .from('flatwork_events')
                    .select('*', { count: 'exact' })
                    .order('id', { ascending: true })
                    .range(from, from + PAGE - 1);

                if (error) throw error;

                const rows = (data ?? []) as FlatworkEvent[];
                all.push(...rows);
                from += rows.length;

                if (rows.length === 0) break;
                if (count !== null && from >= count) break;
                if (count === null && rows.length < PAGE) break;
            }

            return all;
        },
        staleTime: 15_000,
        refetchInterval: 30_000,
    });
}

export interface SetFlatworkScheduleArgs {
    p_job_id: string;
    p_event_types: FlatworkType[];
    p_scheduled_date: string;
    p_crew_id?: string | null;
}

// -----------------------------------------------------------------------------
// set_flatwork_schedule - admin only. Replaces the lot's flatwork rows with one
// per selected type, all sharing the date and crew.
//
// Note "replaces": the RPC deletes the lot's existing rows before inserting.
// That is right for one lot in the dialog where the types are being re-picked,
// and wrong across a selection, which is why the bulk path below never calls it.
// -----------------------------------------------------------------------------
export function useSetFlatworkSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: SetFlatworkScheduleArgs): Promise<FlatworkEvent[]> => {
            const { data, error } = await supabase.rpc('set_flatwork_schedule', args);
            if (error) throw error;
            return (data ?? []) as FlatworkEvent[];
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        },
    });
}

// -----------------------------------------------------------------------------
// clear_flatwork_schedule - admin only. Removes the lot's flatwork records.
// -----------------------------------------------------------------------------
export function useClearFlatworkSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobId: string) => {
            const { error } = await supabase.rpc('clear_flatwork_schedule', { p_job_id: jobId });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        },
    });
}

export interface CompleteFlatworkArgs {
    p_job_id: string;
    p_completion_date: string;
}

// -----------------------------------------------------------------------------
// complete_flatwork - admin only. Marks every flatwork row on the lot complete
// with a shared completion date. This is what lets the job leave the board.
//
// UPDATE only. It raises if the lot has no flatwork rows, which is why the
// dialog saves a schedule first and why the bulk path creates rows for lots
// that have none.
// -----------------------------------------------------------------------------
export function useCompleteFlatwork() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: CompleteFlatworkArgs): Promise<FlatworkEvent[]> => {
            const { data, error } = await supabase.rpc('complete_flatwork', args);
            if (error) throw error;
            return (data ?? []) as FlatworkEvent[];
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        },
    });
}

// -----------------------------------------------------------------------------
// reopen_flatwork - admin only. Undoes a completion (back to 'scheduled').
// Safety net for a mis-marked lot so a job doesn't drop off the board wrongly.
// -----------------------------------------------------------------------------
export function useReopenFlatwork() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobId: string): Promise<FlatworkEvent[]> => {
            const { data, error } = await supabase.rpc('reopen_flatwork', { p_job_id: jobId });
            if (error) throw error;
            return (data ?? []) as FlatworkEvent[];
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        },
    });
}

// -----------------------------------------------------------------------------
// bulk_complete_flatwork - admin only. Many lots, one completion date.
//
// 315 lots sit on the board flagged flatwork_active with no flatwork rows, so
// each one has to be opened and marked by hand before it leaves. Andrea cleared
// 52 across 23 and 24 July at four rows a lot. This is that same action applied
// to a selection, and it is also the answer to her townhome ask: "instead of me
// clicking in to every box to add the date and the status".
//
// Never deletes. Lots that already carry rows get those completed in place with
// their recorded types untouched; only lots with no rows get rows created.
// -----------------------------------------------------------------------------
export function useBulkCompleteFlatwork() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: BulkCompleteFlatworkArgs): Promise<BulkCompleteFlatworkResult> => {
            const { data, error } = await supabase.rpc('bulk_complete_flatwork', args);
            if (error) throw error;
            return data as BulkCompleteFlatworkResult;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}

// -----------------------------------------------------------------------------
// bulk_clear_flatwork_scope - admin only.
//
// For lots flagged flatwork_active that never had flatwork in the first place.
// The flag is set whenever a Pour is updated to complete, regardless of whether
// the lot has any flatwork scope, so foundation-only lots get stuck on the
// board too. Clearing the flag is the honest correction; writing completed
// driveway rows on a lot that never had a driveway is not.
//
// Refuses any lot that already has flatwork rows.
// -----------------------------------------------------------------------------
export function useBulkClearFlatworkScope() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (jobIds: string[]): Promise<BulkClearFlatworkScopeResult> => {
            const { data, error } = await supabase.rpc('bulk_clear_flatwork_scope', {
                p_job_ids: jobIds,
            });
            if (error) throw error;
            return data as BulkClearFlatworkScopeResult;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['flatworkEvents'] });
            qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
            qc.invalidateQueries({ queryKey: ['activityLog'] });
        },
    });
}
